import { create } from 'zustand';

export interface ActiveSession {
  id: string;
  spot: {
    id: string;
    spot_number: string;
    level_label: string;
    level_code: string;
    description: string | null;
    owner: { apartment: string };
  };
  started_at: string;
  available_until: string | null;
  elapsed_seconds: number;
  reason?: string;
  status: string;
  reclaim_alert: ReclaimAlert | null;
}

export interface ReclaimAlert {
  id?: string;            // absent immediately after WS event; populated after session refetch
  sent_at: string;
  acknowledged_at: string | null;
}

export interface AvailabilityCancelled {
  spotId: string;
  spotLabel: string;
  ownerApartment: string;
  timestamp: string;
}

interface SessionState {
  activeSessions: ActiveSession[];
  reclaimAlert:  ReclaimAlert | null;
  window15MinWarning: boolean;
  availabilityCancelled: AvailabilityCancelled | null;
  setActiveSessions: (sessions: ActiveSession[]) => void;
  clearAllSessions: () => void;
  clearSession: (sessionId: string) => void;
  setReclaimAlert:  (alert: ReclaimAlert) => void;
  clearReclaimAlert: () => void;
  setWindow15MinWarning: (warned: boolean) => void;
  setAvailabilityCancelled: (alert: AvailabilityCancelled | null) => void;
  // For backward compatibility
  get activeSession(): ActiveSession | null;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  activeSessions: [],
  reclaimAlert:  null,
  window15MinWarning: false,
  availabilityCancelled: null,

  setActiveSessions: (sessions) => set({ activeSessions: sessions }),
  clearAllSessions: () => set({ activeSessions: [], reclaimAlert: null, window15MinWarning: false, availabilityCancelled: null }),
  clearSession: (sessionId) => set((state) => ({
    activeSessions: state.activeSessions.filter((s) => s.id !== sessionId),
  })),
  setReclaimAlert:  (alert) => set({ reclaimAlert: alert }),
  clearReclaimAlert: () => set({ reclaimAlert: null }),
  setWindow15MinWarning: (warned) => set({ window15MinWarning: warned }),
  setAvailabilityCancelled: (alert) => set({ availabilityCancelled: alert }),

  get activeSession() {
    return get().activeSessions.length > 0 ? get().activeSessions[0] : null;
  },
}));
